import React, { useState } from "react";

import {
  CheckOutlined,
  CloseOutlined,
  DislikeOutlined,
  LikeOutlined,
} from "@ant-design/icons";
import { ProjectLeadActionType, REQUEST_STATUS } from "@src/constants";
import { mapRoleRequestData } from "@src/features/resource_plan/utils/resource_plan_table_utils";
import { isAdditionalInformationUpdated } from "@src/features/role_request_details/utils/role_request_details_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import { clearResourcePlanModal } from "@src/services/slices/resourcePlanModalSlice";
import { useUpdateRoleRequestMutation } from "@src/services/apis/projectsApi";
import { RootState, useAppSelector } from "@src/setupStore";
import {
  Allocation,
  ResourcePlanTableRowData,
  RoleRequest,
  UpdateRoleRequestPayload,
} from "@src/types/role_request_types";
import { App, Button } from "antd";
import { useDispatch } from "react-redux";

import { DeclineConfirmationModal } from "../decline_confirmation_modal";
import { ProposalConfirmationModal } from "../proposal_confirmation_modal";

interface ActionHandlerProps {
  request: RoleRequest;
}

const ActionHandlers: React.FC<ActionHandlerProps> = ({ request }) => {
  const { message } = App.useApp();
  const { gridApi } = useAgGrid();

  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [showProposalModal, setShowProposalModal] = useState<boolean>(false);
  const [actionType, setActionType] = useState<ProjectLeadActionType>(null);

  const dispatch = useDispatch();

  const {
    selectedStandardRoleId,
    jointVenture,
    selectedResourcePlanRequestId,
    additionalInformation,
  } = useAppSelector((state: RootState) => state.resourcePlanDetailsModalSlice);

  const { projectId } = useAppSelector(
    (state: RootState) => state.resourcePlanSlice
  );
  const [updateRoleRequestMutation] = useUpdateRoleRequestMutation();

  /**
   * WIll clear the state of the resource plan modal after the close
   */
  function onClose(): void {
    dispatch(clearResourcePlanModal());
  }

  /**
   * This method will be triggered when the user updates the standard role or in the draft mode also
   * additional information like scope, project identifier or specific role
   */
  async function onApplyChange(): Promise<void> {
    const updateRequestPayload: UpdateRoleRequestPayload = {
      isDraft: request.status === REQUEST_STATUS.DRAFT,
    };

    // Add genericRoleId if a standard role is selected
    if (selectedStandardRoleId) {
      updateRequestPayload.genericRoleId = selectedStandardRoleId;
    }

    // Add additionalInformation fields if available
    if (additionalInformation) {
      Object.assign(updateRequestPayload, additionalInformation);
    }

    // Add isJointVenture if it's explicitly defined (not null or undefined)
    if (jointVenture !== null) {
      updateRequestPayload.isJointVenture = jointVenture;
    }

    try {
      const roleRequest: RoleRequest = await updateRoleRequestMutation({
        projectId: projectId.toString(),
        roleRequestId: selectedResourcePlanRequestId.toString(),
        requestPayload: updateRequestPayload,
      }).unwrap();

      message.success("Changes have been saved successfully");

      const mappedRoleRequest: ResourcePlanTableRowData[] = mapRoleRequestData([
        roleRequest,
      ]);
      gridApi.applyTransaction({ update: mappedRoleRequest });
    } catch (exception) {
      console.error(exception);
    }

    onClose();
  }

  /**
   * Checks if the apply changes button is disabled or not. The button will only be enabled when
   * the user changes the standard role, add another additional information in the draft modal and
   * also when the user checks the joint venture flag in the draft modal
   *
   * @return {boolean} - the state of the disabled status of the button
   */
  function isApplyChangesButtonDisabled(): boolean {
    if (request.status !== REQUEST_STATUS.DRAFT)
      return !isStandardRoleUpdated();

    return (
      !isAdditionalInformationUpdated(additionalInformation, request) &&
      !isJointVentureUpdated() &&
      !isStandardRoleUpdated()
    );
  }

  /**
   * Checks if the standard role is updated and enables the button if this happened.
   *
   * @returns{boolean} - will return true if standard role was updated
   */
  function isStandardRoleUpdated(): boolean {
    if (selectedStandardRoleId === null) return false;

    return selectedStandardRoleId !== request.standardRoleId;
  }

  /**
   * Checks if jointVenture was updated
   *
   * @returns {boolean} - will return true if joint venture was updated
   */
  function isJointVentureUpdated(): boolean {
    if (jointVenture === null) return false;

    return request.jointVenture !== jointVenture;
  }

  /**
   * Function that is called when a declined or proposed request is accepted or declined
   *
   * @param actionType accept or decline
   */
  function onConfirm(actionType: ProjectLeadActionType): void {
    setActionType(actionType);
    setConfirmationModalVisible();
  }

  /**
   * determines which confirmation modal should be triggered and sets it to true
   */
  function setConfirmationModalVisible(): void {
    if (request.status === REQUEST_STATUS.DECLINED) {
      setShowDeclineModal(true);
    } else if (request.status === REQUEST_STATUS.PROPOSED) {
      setShowProposalModal(true);
    }
  }

  /**
   * renders the action buttons of the modal
   *
   * @returns the node with the buttons
   */
  function renderActionButtons(): React.ReactElement | undefined {
    switch (request.status) {
      case REQUEST_STATUS.DRAFT:
      case REQUEST_STATUS.REQUESTED:
      case REQUEST_STATUS.IN_PROGRESS:
        return (
          <Button
            type="primary"
            size="large"
            disabled={isApplyChangesButtonDisabled()}
            onClick={onApplyChange}
            icon={<CheckOutlined />}
          >
            Apply Changes
          </Button>
        );
      case REQUEST_STATUS.PROPOSED:
      case REQUEST_STATUS.DECLINED:
        return (
          <>
            <Button
              type="primary"
              danger
              size="large"
              onClick={() => onConfirm(ProjectLeadActionType.DECLINE)}
              icon={<DislikeOutlined />}
              data-testid="decline-button"
            >
              Decline
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => onConfirm(ProjectLeadActionType.ACCEPT)}
              icon={<LikeOutlined />}
              data-testid="accept-button"
            >
              Accept
            </Button>
          </>
        );
      default:
        return;
    }
  }

  return (
    <>
      <Button size="large" icon={<CloseOutlined />} onClick={onClose}>
        Close
      </Button>
      {renderActionButtons()}

      {showDeclineModal && (
        <DeclineConfirmationModal
          showModal={showDeclineModal}
          onCloseCallback={() => setShowDeclineModal(false)}
          hasDifferentWorkload={request.allocationDetails.some(
            (allocation: Allocation) => !!allocation.proposedPercentage
          )}
          actionType={actionType}
          roleRequestId={request.roleRequestId}
        />
      )}
      {showProposalModal && (
        <ProposalConfirmationModal
          showModal={showProposalModal}
          onCloseCallback={() => setShowProposalModal(false)}
          actionType={actionType}
          roleRequestId={request.roleRequestId}
          standardRoleName={request.standardRoleName}
        />
      )}
    </>
  );
};

export default ActionHandlers;
